import * as signalR from "@aspnet/signalr";
import {
    ConsoleFormattedStream,
    createLogger,
    stdSerializers
} from "browser-bunyan";
import * as React from "react";

import Auth from "./login/auth";
import { Header, IUserInfoProps } from "./login/userinfo";

import ClientStore from "./data/client";
import DatabaseStore from "./data/database";
import ItemStore from "./data/item";
import UserStore from "./data/user";

import { ITaskProps } from "./bits/task";
import Clients from "./clients/clients";
import Database from "./database/database";
import Items from "./items/items";
import { AppSection, Menu } from "./menu";
import Users from "./users/users";

const url = process.env.REACT_APP_API_BYGGDATA_BASE_URL + "signalr/admin";
const log = createLogger({
    name: 'console',
    serializers: stdSerializers,
    src: true,
    streams: [
        {
            level: "trace",
            stream: new ConsoleFormattedStream()
        }
    ]
});

interface IAppState {
    Section: AppSection
    UserInfo: IUserInfoProps
    Connection?: signalR.HubConnection
    Log: string[]
    Tasks: { [id: string]: ITaskProps }
}

class App extends React.Component<{}, IAppState> {
    private auth: Auth = new Auth();
    private logBottom: HTMLDivElement | null = null;
    private renewRef = React.createRef<HTMLIFrameElement>();

    private store = {
        client: new ClientStore(this.auth.fetch, log),
        database: new DatabaseStore(this.auth.fetch, log),
        item: new ItemStore(this.auth.fetch, log),
        user: new UserStore(this.auth.fetch)
    };

    constructor(props: {}) {
        super(props);
        this.state = this.defaultState();
        this.onTokenRenewed = this.onTokenRenewed.bind(this);
        document.addEventListener("tokenRenewed", this.onTokenRenewed);

        // If redirected from logon page, process the access token
        if (location.hash.indexOf("access_token") > 0) {
            this.auth.getToken(window.location).then(this.handleLogin)
        }
        // Otherwise, if the access token is still valid, fetch user data
        else if (this.auth.hasValidToken()) {
            this.state = {
                ...this.state,
                Connection:
                    new signalR.HubConnectionBuilder().withUrl(url +
                        (this.auth.token ?
                            ("?access_token=" + this.auth.token.accessToken) : "")).build(),
                Section: AppSection.Users
            };
            this.state.UserInfo.LoggedIn = true;
            this.fetchUserInfo();
        }
        this.setupConnection();
    }

    public componentDidUpdate() {
        if (this.logBottom) {
            this.logBottom.scrollIntoView();
        }
    }

    public render() {
        return <div>
            <Header {...this.state.UserInfo} />
            {
                this.auth.hasValidToken() &&
                <Menu setSection={this.setSection} />
            }
            {(() => {
                switch (this.state.Section) {
                    case AppSection.Default:
                        return <div className="content">
                            <h1>Ikke pålogget</h1>
                            Logg på og forsøk igjen.
                        </div>
                    case AppSection.Users:
                        return <Users data={this.store.user} handleError={this.handleError} />
                    case AppSection.Clients:
                        return <Clients data={this.store.client} handleError={this.handleError} />
                    case AppSection.Lists:
                        return <Items data={this.store.item} handleError={this.handleError} />
                    case AppSection.Database:
                        return <Database data={this.store.database} tasks={this.state.Tasks} handleError={this.handleError} />
                }
            })()}
            <div className="log">
                <button style={{ margin: "15px" }} onClick={this.requestTokenRenew}>Renew token</button>
                <ul>
                    {this.state.Log.map((x, i) => <li key={i}>{x}</li>)}
                    <div ref={e => this.logBottom = e} />
                </ul>
            </div>
            <iframe ref={this.renewRef} src="./renew" style={{ width: 0, height: 0, border: 0, display: "none" }} />
        </div>;
    }

    private handleError = (e: any) => {
        this.auth.clearToken();
        this.setState(this.defaultState());
    }

    private handleLogin = () => {
        // Clear the address bar
        history.replaceState("", document.title, window.location.pathname + window.location.search);

        this.setState({
            Connection: new signalR.HubConnectionBuilder().withUrl(url +
                (this.auth.token ?
                    ("?access_token=" + this.auth.token.accessToken) : "")).build(),
            Section: AppSection.Users,
            UserInfo: {
                ...this.state.UserInfo,
                LoggedIn: true
            }
        }, () => {
            this.fetchUserInfo();
            this.setupConnection()
        });
    }

    private redirectToLogin = () => {
        location.href = this.auth.getLogInUri();
    }

    private redirectToLogOut = () => {
        const logoutUri = this.auth.getLogOutUri();
        this.auth.clearToken();
        location.href = logoutUri;
    }

    private requestTokenRenew = () =>
        this.renewRef.current!.contentDocument!
            .dispatchEvent(new CustomEvent("requestTokenRenew"));

    private defaultState() {
        return {
            Log: [],
            Section: AppSection.Default,
            Tasks: {},
            UserInfo: {
                LogIn: this.redirectToLogin,
                LoggedIn: false,
                handleError: this.handleError,
                handleLogOut: this.redirectToLogOut
            }
        }
    };

    private fetchUserInfo = () => {
        if (this.auth.hasValidToken()) {
            this.store.user.GetUserInfo()
                .then(info =>
                    this.setState({
                        UserInfo: {
                            ...this.state.UserInfo,
                            Name: info && info.name || ""
                        }
                    }))
                .catch(this.handleError);
        }
    }

    private setupConnection = () => {
        if (this.state.Connection) {
            this.state.Connection.on("progress", (props: ITaskProps) => {
                this.state.Tasks[props.id] = props;
                this.setState({
                    Tasks: this.state.Tasks
                });
            });
            this.state.Connection.on("log", (message: string) => {
                this.state.Log.push(message);
                this.setState({ Log: this.state.Log });
            });
            this.state.Connection.start();
        }
    }

    private setSection = (s: AppSection) => this.setState({ Section: s });

    private onTokenRenewed() {
        log.debug("Token renewal notification received, updating token in sessionStorage");
        log.debug("Old token was " + this.auth.token!.accessToken);
        log.debug("Old expiry: " + (this.auth.token! as any).expires);
        this.auth.refreshToken();
        log.debug("New token is " + this.auth.token!.accessToken);
        log.debug("New expiry: " + (this.auth.token! as any).expires);
    }
}

export default App;


// WEBPACK FOOTER //
// ./src/app.tsx